import type { InvitationView } from '@p2b/contract';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { useMe } from '../App.js';
import { api, authClient, P2bError } from '../api.js';
import { formatDay, label, useI18n } from '../i18n.js';

/** The page an invitation email links to: what the invitation is, then sign in (if needed) and accept. */
export function Invite() {
  const { t } = useI18n();
  const { id } = useParams();
  const me = useMe();
  const preview = useQuery({
    queryKey: ['invitation', id],
    queryFn: () => api.invitation(id!),
    retry: false,
  });

  if (preview.isPending || me.isPending) return <div className="center muted">{t('loading')}</div>;
  if (preview.error || !preview.data) {
    return (
      <div className="stack">
        <h2 className="bare-title">{t('invitationTitle')}</h2>
        <p className="error" role="alert">
          {preview.error instanceof P2bError && preview.error.status === 404
            ? t('invitationGone')
            : String(preview.error)}
        </p>
      </div>
    );
  }
  const inv = preview.data;
  const signedIn = Boolean(me.data);
  const sameUser = signedIn && me.data!.user.email.toLowerCase() === inv.email.toLowerCase();
  const loginHref = `/login?${new URLSearchParams({ email: inv.email, next: `/invite/${inv.id}` })}`;

  return (
    <div className="stack">
      <h2 className="bare-title">{t('invitationTitle')}</h2>
      <p>
        <strong>{inv.inviterName ?? 'Mynstra'}</strong> {t('inviteIntro')}
      </p>
      <InvitationFacts inv={inv} />
      {inv.status !== 'pending' ? (
        <p className="error" role="alert">
          {t('invitationGone')} ({label(t, 'status', inv.status)})
        </p>
      ) : !signedIn ? (
        <Link className="button primary block" to={loginHref}>
          {t('signInToAccept')}
        </Link>
      ) : !sameUser ? (
        <>
          <p className="error" role="alert">
            {t('wrongAccount')}
          </p>
          <p className="muted">{me.data!.user.email}</p>
          <button
            className="block"
            onClick={async () => {
              await authClient.signOut();
              window.location.assign(loginHref);
            }}
          >
            {t('switchAccount')}
          </button>
        </>
      ) : (
        <AcceptButton inv={inv} />
      )}
    </div>
  );
}

/** What the invitation gives: the organization and any projects, each with its role; for whom and until when. */
export function InvitationFacts({ inv }: { inv: InvitationView }) {
  const { t, lang } = useI18n();
  return (
    <dl className="facts">
      <dt>{t('organization')}</dt>
      <dd>
        <strong>{inv.orgName}</strong> <span className="muted">{label(t, 'role', inv.role)}</span>
      </dd>
      {inv.grants.length > 0 && (
        <>
          <dt>{t('projects')}</dt>
          <dd>
            {inv.grants.map((g) => (
              <div key={g.projectId}>
                <strong>{g.projectName}</strong> <span className="muted">{label(t, 'role', g.role)}</span>
              </div>
            ))}
          </dd>
        </>
      )}
      <dt>{t('inviteFor')}</dt>
      <dd>{inv.email}</dd>
      <dt>{t('validUntil')}</dt>
      <dd>{formatDay(inv.expiresAt, lang)}</dd>
    </dl>
  );
}

/** Accept, then go to the invited project (or home). */
export function AcceptButton({ inv }: { inv: InvitationView }) {
  const { t } = useI18n();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  async function accept() {
    setBusy(true);
    setError(null);
    const res = await authClient.organization.acceptInvitation({ invitationId: inv.id });
    if (res.error) {
      setBusy(false);
      setError(res.error.message ?? 'error');
      return;
    }
    await qc.invalidateQueries();
    const first = inv.grants[0];
    if (first) {
      const projects = await api.projects();
      const p = projects.find((x) => x.id === first.projectId);
      if (p) {
        navigate(`/projects/${p.org}/${p.slug}`);
        return;
      }
    }
    navigate(inv.orgSlug ? `/orgs/${inv.orgSlug}` : '/');
  }
  return (
    <>
      {error && (
        <p className="error" role="alert">
          {error}
        </p>
      )}
      <button className="primary" onClick={accept} disabled={busy}>
        {t('acceptAndOpen')}
      </button>
    </>
  );
}
